import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RootState, useAppDispatch } from '../../../store';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { createDataSourceSSE, fetchDataSources, resetDataSourceCreation } from '../../../store/actions/AIStudioAction';
import { fetchLinkableProjects } from '../../../store/actions/Projects/ProjectActions';
import { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import IndexCreationProgress from '../../shared/SSECreationProgress';
import { useSelector } from 'react-redux';
import BaseForm from '../../shared/FormComponents/BaseForm';
import { FormSchema } from '../../../utils/validation/form-schema';
import OrgWideToggle from '../../shared/FormComponents/OrgWideToggle';
import { getMultiHubSocketService } from '../../../utils/MultiHubSocketService';
import { DATA_SOURCE_CREATE_COMPLETED, DATA_SOURCE_CREATE_FAILED, DATA_SOURCE_CREATE_STEP, DATA_SOURCE_CREATE_STEPS } from '../../../store/types';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, createDataSourceFormTourSteps } from '../../shared/FeatureTour';

interface CreateDataSourceFormProps {
  onCancel: () => void;
}

const CreateDataSourceForm: React.FC<CreateDataSourceFormProps> = ({ onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();

  const [isCreating, setIsCreating] = useState(false);
  const [isOrgWide, setIsOrgWide] = useState(false);
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'create-data-source-form',
    steps: createDataSourceFormTourSteps,
    readyToShow: !isCreating,
    autoStartDelay: 600,
  });

  const { complete, connected, steps, isConnecting, ok, summary, startedAt } = useSelector((state: RootState) => state.aistudio);

  const createFormSections = useMemo(() => [
    {
      title: t('Data Source Details'),
      description: t('Provide the basic information for your data source.'),
      dataTourId: 'ds-create-details',
      fields: [
        {
          name: 'dataSourceName',
          type: 'input' as const,
          label: t('Data Source Name'),
          placeholder: t('Enter data source name'),
          required: true,
          inputType: 'text',
        },
      ],
    },
    {
      title: t('Sharing'),
      description: t('Control who can access this data source.'),
      dataTourId: 'ds-create-sharing',
      fields: [
        {
          name: 'is_org_wide',
          type: 'custom' as const,
          label: '',
          gridSize: 'full' as const,
          customComponent: OrgWideToggle,
          customProps: { title: t('Organization-wide data source') },
        },
        ...(!isOrgWide ? [
          {
            name: 'collaborators',
            type: 'collaboratorSelector' as const,
            label: t('Access'),
            description: t('Add people who can access this data source'),
            customProps: {
              title: t('Share with team members'),
              ownerLabel: t('You are the data source owner'),
              showOwnerSection: true,
            },
          },
        ] : []),
      ],
    },
    ...(projectOptions.length > 0 ? [{
      title: t('Link to Project'),
      description: t('Optionally link this data source to an existing project.'),
      fields: [
        {
          name: 'project_uuid',
          type: 'dropdown' as const,
          label: t('Project'),
          placeholder: t('Select a project'),
          required: false,
          customProps: {fullWidth: true} as any,
          gridSize: 'full' as const,
          options: projectOptions,
        },
      ],
    }] : []),
    {
      title: t('Upload Documents'),
      description: t('Upload files to include in this data source. Supported formats: PDF, DOCX, TXT, MD'),
      dataTourId: 'ds-create-documents',
      fields: [
        {
          name: 'files',
          type: 'fileUploader' as const,
          label: t('Documents'),
          required: true,
        },
      ],
    },
  ], [t, isOrgWide, projectOptions]);

  const createFormSchema = useMemo(() => {
    const fields: any = {
      dataSourceName: (value: any) => {
        if (!value || !String(value).trim()) {
          return { isValid: false, error: t('Data source name is required') };
        }
        return { isValid: true };
      },
      is_org_wide: () => ({ isValid: true }),
      files: (value: any) => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return { isValid: false, error: t('At least one file is required') };
        }
        return { isValid: true };
      },
      collaborators: (value: any) => ({ isValid: true }),
    };
    return new FormSchema(fields);
  }, [t]);

  const pubsubCleanupRef = useRef<(() => void) | null>(null);

  // Fetch linkable projects on mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsResp = await dispatch(fetchLinkableProjects() as any);
        if (projectsResp && Array.isArray(projectsResp)) {
          setProjectOptions(projectsResp.map((p: any) => ({ value: p.uuid, label: p.title })));
        }
      } catch (error: any) {
        console.error('[CreateDataSourceForm] Error loading projects:', error?.message);
      }
    };
    loadProjects();
  }, [dispatch]);

  // Cleanup PubSub on unmount and reset creation state
  useEffect(() => {
    return () => {
      if (pubsubCleanupRef.current) {
        pubsubCleanupRef.current();
      }
      dispatch(resetDataSourceCreation());
    };
  }, [dispatch]);

  // Step ID mapping from AI Engine step names to frontend step IDs
  const STEP_NAME_TO_ID: Record<string, string> = {
    'Validating Data Source': 'validate',
    'Creating Search Index': 'search_index',
    'Processing Documents': 'process_docs',
    'Generating Metadata': 'metadata',
    'Finalizing': 'finalize',
  };

  const connectToPubSub = useCallback(async (
    correlationId: string, 
    dataSourceUuid: string,
  ) => {
    const hub = 'ai_processing';
    const group = `datasource_${correlationId}`;
    const socketService = getMultiHubSocketService();

    try {
      const connected = await socketService.connectToHub(hub);
      if (!connected) {
        console.warn('[CreateDataSourceForm] Failed to connect to PubSub hub');
        return;
      }

      // Join the datasource processing group
      socketService.emit(hub, 'join', { group });

      // Listen for all events (wildcard)
      const handleEvent = (eventData: any, eventName?: string) => {
        // Only handle events for our correlation ID
        if (eventData?.correlationId !== correlationId && eventData?.correlation_id !== correlationId) {
          return;
        }

        const subType = eventData?.data?.sub_type || eventData?.sub_type || '';
        const stepName = eventData?.data?.step_name || eventData?.stepName || '';
        const message = eventData?.message || '';
        const stepId = STEP_NAME_TO_ID[stepName] || stepName;

        if (subType === 'step_started') {
          dispatch({ type: DATA_SOURCE_CREATE_STEP, payload: { id: stepId, status: 'in_progress', message } });
        } else if (subType === 'step_progress') {
          dispatch({ type: DATA_SOURCE_CREATE_STEP, payload: { id: stepId, status: 'in_progress', message } });
        } else if (subType === 'step_completed') {
          dispatch({ type: DATA_SOURCE_CREATE_STEP, payload: { id: stepId, status: 'completed', message } });
        } else if (subType === 'step_failed') {
          dispatch({ type: DATA_SOURCE_CREATE_STEP, payload: { id: stepId, status: 'failed', message } });
        } else if (subType === 'completed') {
          // Full pipeline complete
          dispatch({ type: DATA_SOURCE_CREATE_COMPLETED, payload: { ok: true, uuid: dataSourceUuid } });
          dispatch(showFeedbackModal({
            showModal: true,
            message: 'Data source created successfully.',
            modalType: 'success',
            duration: 3000,
          }));
          dispatch(fetchDataSources());
          
          // Navigate to the data source after a brief delay
          setTimeout(() => {
            history.push(`/data-sources/${dataSourceUuid}`);
          }, 1500);
        } else if (subType === 'failed') {
          const error = eventData?.data?.error || message || 'Processing failed';
          dispatch({ type: DATA_SOURCE_CREATE_FAILED, payload: { error } });
          dispatch(showFeedbackModal({
            showModal: true,
            message: error,
            modalType: 'error',
            duration: 4000,
          }));
        }
      };

      socketService.on(hub, '*', handleEvent);

      // Store cleanup function
      pubsubCleanupRef.current = () => {
        socketService.off(hub, '*', handleEvent);
        socketService.emit(hub, 'leave', { group });
      };

    } catch (err) {
      console.error('[CreateDataSourceForm] PubSub connection error:', err);
    }
  }, [dispatch, history]);

  const handleCreateSubmit = useCallback(async (formValues: Record<string, any>) => {
    if (!checkAiFeatureGate('data_source_indexing')) return;
    const { dataSourceName, is_org_wide, collaborators, files, project_uuid } = formValues;

    setIsCreating(true);

    const orgWide = !!is_org_wide;
    const dataSourceType = orgWide ? 'Company' :
                          (collaborators && collaborators.length > 0) ? 'Shared' : 'Personal';
    // For org-wide, backend auto-adds all org users — don't send sharedWith
    const sharedWith = !orgWide && collaborators && collaborators.length > 0
      ? collaborators.map((c: any) => c.collaborator_uuid || String(c.user_id))
      : undefined;

    try {
      const response = await dispatch(createDataSourceSSE(dataSourceName, files, dataSourceType, sharedWith, project_uuid || undefined) as any);

      if (response?.status === 200 && response?.data?.uuid) {
        const { uuid, correlationId } = response.data;
        
        // Connect to PubSub for live progress updates from AI Engine
        if (correlationId) {
          await connectToPubSub(correlationId, uuid);
        } else {
          // No PubSub available — mark as completed immediately
          dispatch({ type: DATA_SOURCE_CREATE_COMPLETED, payload: { ok: true, uuid } });
          dispatch(fetchDataSources());
          setIsCreating(false);
          history.push(`/data-sources/${uuid}`);
        }
        return;
      }
      setIsCreating(false);
    } catch (err) {
      setIsCreating(false);
    }
    onCancel();
  }, [dispatch, history, onCancel, connectToPubSub]);

  const handleValuesChange = useCallback((values: Record<string, any>, changedField: string) => {
    if (changedField === 'is_org_wide') {
      setIsOrgWide(!!values.is_org_wide);
    }
  }, []);

  if (isCreating) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <IndexCreationProgress
          title={t('Creating Data Source')}
          isConnecting={isConnecting}
          steps={steps}
          connected={connected}
          complete={complete}
          ok={ok}
          summary={summary}
          startedAt={startedAt}
          onCancel={onCancel}
          type='data source'
        />
      </div>
    );
  }

  return (
    <>
    <BaseForm
      title={t('Create New Data Source')}
      description={t('Fill out this form to add your data source.')}
      sections={createFormSections}
      schema={createFormSchema}
      initialValues={{
        dataSourceName: '',
        is_org_wide: false,
        collaborators: [],
        files: [],
        project_uuid: '',
      }}
      onSubmit={handleCreateSubmit}
      onCancel={onCancel}
      submitLabel={t('Create Data Source')}
      isSubmitting={isCreating}
      hideLoadingOverlay={true}
      onValuesChange={handleValuesChange}
      submitDisabled={!hasAiFeatureAccess('data_source_indexing')}
      submitTooltip={t('Upgrade your plan to use AI Data Sources.')}
    />
    <FeatureTour
      steps={createDataSourceFormTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default CreateDataSourceForm;